import React, { useState } from 'react';
import { CopyOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { message } from 'antd';

/**
 * SimpleJsonViewer - A lightweight JSON viewer component with collapse/expand
 * No external dependencies, works with React 19
 */
const SimpleJsonViewer = ({ data, name = 'root', collapsed = 1, level = 0 }) => {
  const [isCollapsed, setIsCollapsed] = useState(level < collapsed ? false : true);

  if (data === null) {
    return <span style={{ color: '#808080' }}>null</span>;
  }

  if (data === undefined) {
    return <span style={{ color: '#808080' }}>undefined</span>;
  }

  const dataType = typeof data;

  // Handle primitives
  if (dataType === 'string') {
    return <span style={{ color: '#0B7285' }}>"{data}"</span>;
  }

  if (dataType === 'number') {
    return <span style={{ color: '#1971C2' }}>{data}</span>;
  }

  if (dataType === 'boolean') {
    return <span style={{ color: '#862E9C' }}>{data.toString()}</span>;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span style={{ color: '#495057' }}>[]</span>;
    }

    return (
      <div style={{ marginLeft: level > 0 ? '20px' : '0' }}>
        <span
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {isCollapsed ? <RightOutlined style={{ fontSize: '10px', marginRight: '4px' }} /> : <DownOutlined style={{ fontSize: '10px', marginRight: '4px' }} />}
          <span style={{ color: '#495057' }}>[{data.length}]</span>
        </span>
        {!isCollapsed && (
          <div style={{ marginLeft: '20px' }}>
            {data.map((item, index) => (
              <div key={index} style={{ marginTop: '4px' }}>
                <span style={{ color: '#868E96', fontSize: '12px' }}>{index}: </span>
                <SimpleJsonViewer data={item} name={index.toString()} collapsed={collapsed} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Handle objects
  if (dataType === 'object') {
    const keys = Object.keys(data);
    if (keys.length === 0) {
      return <span style={{ color: '#495057' }}>{'{}'}</span>;
    }

    const handleCopy = () => {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      message.success('Copied to clipboard');
    };

    return (
      <div style={{ marginLeft: level > 0 ? '20px' : '0' }}>
        <span>
          <span
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {isCollapsed ? <RightOutlined style={{ fontSize: '10px', marginRight: '4px' }} /> : <DownOutlined style={{ fontSize: '10px', marginRight: '4px' }} />}
            {level === 0 && name && <span style={{ color: '#495057', fontWeight: 'bold' }}>{name} </span>}
            <span style={{ color: '#495057' }}>{`{${keys.length}}`}</span>
          </span>
          {level === 0 && (
            <CopyOutlined
              onClick={handleCopy}
              style={{ marginLeft: '8px', cursor: 'pointer', color: '#1890ff', fontSize: '12px' }}
            />
          )}
        </span>
        {!isCollapsed && (
          <div style={{ marginLeft: '20px' }}>
            {keys.map((key) => (
              <div key={key} style={{ marginTop: '4px' }}>
                <span style={{ color: '#D9480F', fontSize: '12px' }}>"{key}"</span>
                <span style={{ color: '#868E96' }}>: </span>
                <SimpleJsonViewer data={data[key]} name={key} collapsed={collapsed} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return <span>{String(data)}</span>;
};

export default SimpleJsonViewer;